import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import { getLogoUrl } from '@/pages/organization/systems/components/graph-3d-utils';
import type { DashboardSystem } from '@bliks-io/contracts';

interface DashboardSystemsTableProps {
  systems: DashboardSystem[];
}

export function DashboardSystemsTable({ systems }: DashboardSystemsTableProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const [showAll, setShowAll] = useState(false);

  const { sorted, maxUsage } = useMemo(() => {
    const s = [...systems].sort((a, b) => b.usageHours - a.usageHours);
    return { sorted: s, maxUsage: Math.max(...s.map((x) => x.usageHours), 1) };
  }, [systems]);
  const visibleSystems = showAll ? sorted : sorted.slice(0, 3);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg">{t('dashboard.systems.title')}</CardTitle>
        {sorted.length > 3 && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-primary hover:underline"
          >
            {showAll ? t('common.showLess') : t('common.viewAll')}
          </button>
        )}
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.systems.noSystems')}</p>
        ) : (
          <div className="space-y-1">
            {visibleSystems.map((sys) => {
              const logoUrl = getLogoUrl(sys.logo ?? undefined);
              return (
                <Link
                  key={sys._id}
                  to="/$slug/systems/$systemId"
                  params={{ slug, systemId: sys._id }}
                  className="flex items-center gap-3 rounded-md border p-3 transition-colors hover:bg-muted/50"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-info-surface-strong text-sm font-semibold text-info">
                    {logoUrl ? (
                      <img src={logoUrl} alt={sys.name} className="h-6 w-6 object-contain" />
                    ) : (
                      sys.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{sys.name}</p>
                    {sys.category && (
                      <p className="text-xs text-muted-foreground">{sys.category}</p>
                    )}
                  </div>
                  <div className="h-2 w-16 shrink-0 rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-info-solid"
                      style={{
                        width: `${(sys.usageHours / maxUsage) * 100}%`
                      }}
                    />
                  </div>
                  <span className="w-8 shrink-0 text-right text-xs text-muted-foreground">
                    {t('dashboard.hoursSuffix', { value: Math.round(sys.usageHours) })}
                  </span>
                  <div className="flex min-w-[90px] shrink-0 justify-end">
                    <Badge variant="outline" className="whitespace-nowrap">
                      {t('dashboard.systems.processCount', {
                        count: sys.processCount
                      })}
                    </Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
